import { useState, useEffect } from '@wordpress/element';
import {
    PanelBody,
    SelectControl,
    Button,
    __experimentalNumberControl as NumberControl
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';

const FloorSelectionPanel = ({ selectedFloors, setAttributes }) => {
    // State for floor selection
    const [selectedFloor, setSelectedFloor] = useState('');
    const [floorOptions, setFloorOptions] = useState([
        { label: 'Select a floor', value: '' }
    ]);

    // Fetch floor data from API
    useEffect(() => {
        apiFetch({ path: '/valve/v1/blocks/' })
            .then((data) => {
                const floors = data.floors?.floor_select || {};
                const options = [
                    { label: 'Select a floor', value: '' },
                    ...Object.entries(floors).map(([value, label]) => ({ label, value }))
                ];
                setFloorOptions(options);
            })
            .catch((error) => {
                console.error('Error fetching floor data:', error);
            });
    }, []);

    // Function to add a selected floor to the list
    const addFloor = () => {
        if (!selectedFloor) return;

        // Get the label for the selected floor
        const option = floorOptions.find(opt => opt.value === selectedFloor);
        if (!option) return;

        const newFloor = {
            id: Date.now(), // unique ID for this selection
            floorId: selectedFloor,
            floorName: option.label,
            floorWidth: 300,
            positionTop: 0,
            positionLeft: 0,
            isOpen: true // New floor is open by default
        };

        const updatedFloors = [...selectedFloors, newFloor];
        setAttributes({ selectedFloors: updatedFloors });

        // After adding, reset the select to default
        setSelectedFloor('');
    };

    // Function to remove a floor from the list
    const removeFloor = (id) => {
        const updatedFloors = selectedFloors.filter(floor => floor.id !== id);
        setAttributes({ selectedFloors: updatedFloors });
    };

    // Function to toggle floor collapse state
    const toggleFloorCollapse = (id) => {
      const updatedFloors = selectedFloors.map(floor => {
          if (floor.id === id) {
              return { ...floor, isOpen: !floor.isOpen };
          }
          return floor;
      });
      setAttributes({ selectedFloors: updatedFloors });
    };

    // Function to update floor properties
    const updateFloorProperty = (id, property, value) => {
        const updatedFloors = selectedFloors.map(floor => {
            if (floor.id === id) {
                return { ...floor, [property]: value };
            }
            return floor;
        });
        setAttributes({ selectedFloors: updatedFloors });
    };

    return (
        <PanelBody title={__('Floor Selection')} initialOpen={true}>
            <SelectControl
                label={__('Select Floor')}
                value={selectedFloor}
                options={floorOptions}
                onChange={(value) => setSelectedFloor(value)}
            />
            <Button
                variant="primary"
                onClick={addFloor}
                disabled={!selectedFloor}
            >
                {__('Add Floor')}
            </Button>

            {selectedFloors.length > 0 && (
                <div className="selected-floors-container">
                    <h4>{__('Selected Floors')}</h4>
                    {selectedFloors.map((floor) => (
                        <div key={floor.id} className="floor-item">
                            <div className="floor-header" onClick={() => toggleFloorCollapse(floor.id)}>
                                <div className="floor-title">{floor.floorName}</div>
                                <Button>
                                {floor.isOpen ? (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16">
                                        <path d="M233.4 105.4c12.5-12.5 32.8-12.5 45.3 0l192 192c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L256 173.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l192-192z"/>
                                    </svg>
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="16" height="16">
                                        <path d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/>
                                    </svg>
                                )}
                                </Button>
                            </div>

                            {floor.isOpen && (
                                <div className="floor-content">
                                    <p className="floor-instructions">
                                        {__('The default width of floor block is 300px, however, there might be some special floors that should be wider. Use the following fields to change the width of current floor and properly position it in relation to other blocks.', 'interactive-demo')}
                                    </p>

                                    <NumberControl
                                        label={__('Floor Width', 'interactive-demo')}
                                        value={floor.floorWidth}
                                        onChange={(value) => updateFloorProperty(floor.id, 'floorWidth', Number(value))}
                                        min={1}
                                        step={1}
                                        className="floor-fields"
                                    />

                                    <NumberControl
                                        label={__('Position Top', 'interactive-demo')}
                                        value={floor.positionTop}
                                        onChange={(value) => updateFloorProperty(floor.id, 'positionTop', Number(value))}
                                        step={1}
                                        className="floor-fields"
                                    />

                                    <NumberControl
                                        label={__('Position Left', 'interactive-demo')}
                                        value={floor.positionLeft}
                                        onChange={(value) => updateFloorProperty(floor.id, 'positionLeft', Number(value))}
                                        step={1}
                                        className="floor-fields"
                                    />

                                    <Button
                                        isDestructive
                                        onClick={() => removeFloor(floor.id)}
                                        className="floor-remove-btn components-button editor-post-trash is-next-40px-default-size is-secondary is-destructive"
                                    >
                                        {__('Remove Floor', 'interactive-demo')}
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </PanelBody>
    );
};

export default FloorSelectionPanel;
